import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { XMarkIcon, KeyIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { crmApi } from '../services/crmApi';
import { CrmCredentialsRequest, CrmCredentialsResponse } from '../types';

interface CrmCredentialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  agentType: 'assistant' | 'workflow';
  agentName: string;
  onCredentialsUpdated: () => void;
}

const CrmCredentialsModal: React.FC<CrmCredentialsModalProps> = ({
  isOpen,
  onClose,
  agentId,
  agentType,
  agentName,
  onCredentialsUpdated
}) => {
  const [credentials, setCredentials] = useState<CrmCredentialsRequest>({
    servicebridge_username: '',
    servicebridge_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasExistingCredentials, setHasExistingCredentials] = useState(false);
  const [existingUsername, setExistingUsername] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadExistingCredentials();
    }
  }, [isOpen, agentId, agentType]);

  const loadExistingCredentials = async () => {
    try {
      let response: CrmCredentialsResponse;
      if (agentType === 'assistant') {
        response = await crmApi.getAssistantCrmCredentials(agentId);
      } else {
        response = await crmApi.getWorkflowCrmCredentials(agentId);
      }

      setHasExistingCredentials(response.has_credentials);
      if (response.servicebridge_username) {
        setExistingUsername(response.servicebridge_username);
        setCredentials(prev => ({
          ...prev,
          servicebridge_username: response.servicebridge_username || ''
        }));
      }
    } catch (error) {
      console.error('Error loading existing credentials:', error);
    }
  };

  const handleSave = async () => {
    if (!credentials.servicebridge_username.trim() || !credentials.servicebridge_password.trim()) {
      toast.error('Please fill in both username and password');
      return;
    }

    setLoading(true);
    try {
      let response: CrmCredentialsResponse;
      if (hasExistingCredentials) {
        // Update existing credentials
        if (agentType === 'assistant') {
          response = await crmApi.updateAssistantCrmCredentials(agentId, credentials);
        } else {
          response = await crmApi.updateWorkflowCrmCredentials(agentId, credentials);
        }
      } else {
        // Set new credentials
        if (agentType === 'assistant') {
          response = await crmApi.setAssistantCrmCredentials(agentId, credentials);
        } else {
          response = await crmApi.setWorkflowCrmCredentials(agentId, credentials);
        }
      }

      if (response.success) {
        toast.success(response.message);
        onCredentialsUpdated();
        onClose();
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save CRM credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!hasExistingCredentials) return;

    if (!window.confirm('Are you sure you want to delete the CRM credentials? This will disable CRM integration for this agent.')) {
      return;
    }

    setLoading(true);
    try {
      let response: CrmCredentialsResponse;
      if (agentType === 'assistant') {
        response = await crmApi.deleteAssistantCrmCredentials(agentId);
      } else {
        response = await crmApi.deleteWorkflowCrmCredentials(agentId);
      }

      if (response.success) {
        toast.success(response.message);
        onCredentialsUpdated();
        onClose();
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete CRM credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCredentials({
      servicebridge_username: '',
      servicebridge_password: ''
    });
    setShowPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <KeyIcon className="h-6 w-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              {hasExistingCredentials ? 'Update' : 'Set'} CRM Credentials
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Configure ServiceBridge CRM credentials for <strong>{agentName}</strong> ({agentType})
          </p>
          {hasExistingCredentials && (
            <p className="text-sm text-green-600 mt-1">
              ✓ CRM credentials are currently configured
            </p>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ServiceBridge Username
            </label>
            <input
              type="text"
              value={credentials.servicebridge_username}
              onChange={(e) => setCredentials(prev => ({
                ...prev,
                servicebridge_username: e.target.value
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter ServiceBridge username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ServiceBridge Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={credentials.servicebridge_password}
                onChange={(e) => setCredentials(prev => ({
                  ...prev,
                  servicebridge_password: e.target.value
                }))}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter ServiceBridge password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <EyeIcon className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-6">
          <div>
            {hasExistingCredentials && (
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                Delete Credentials
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Saving...' : hasExistingCredentials ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmCredentialsModal;